/**
 * The browser's calls to the Lattice API. Every request is same-origin under /api, so the session
 * cookie Better Auth sets travels with it. In development Vite forwards /api to the Worker.
 */
import type {
  AccountFormulaEdit,
  AccountFormulas,
  AccountLabs,
  AccountPortal,
  AccountSubscription,
  CheckoutView,
  AddClinicPatientReply,
  ClinicAccessList,
  ClinicFormulaChange,
  ClinicFormulaEdit,
  ClinicFormulaView,
  ClinicHome,
  ClinicLinkRequestView,
  ClinicPatientDetail,
  ClinicPatientList,
  ClinicRole,
  ClinicShareScope,
  ClinicInvitationInfo,
  ClinicSnapshot,
  CustomerOrders,
  CurrentFormula,
  DeletionStatus,
  IntakeAnswers,
  ProcessingProgress,
  QuestionId,
  ShippingAddress,
  MeView,
} from "@lattice/core";
import {
  MAX_LAB_FILE_BYTES,
  readAccountFormulaEdit,
  readAccountFormulas,
  readAccountLabs,
  readAccountPortal,
  readAccountSubscription,
  readClinicAccessList,
  readCustomerOrders,
  readDeletionStatus,
  readIntake,
  readIntakeSave,
} from "@lattice/core";

/** The header the Worker and Better Auth read a Turnstile token from. */
const TURNSTILE_HEADER = "x-captcha-response";

/** How long a load may take before it counts as failed, so a hung request reaches the failure screen. */
const LOAD_LIMIT_MS = 20_000;

/**
 * A GET that gives up after LOAD_LIMIT_MS. The abort rejects, and every loader treats a rejection as
 * a failed load, so a request that never answers ends in the retry screen and not a spinner.
 */
async function fetchWithin(path: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOAD_LIMIT_MS);

  try {
    return await fetch(path, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function signedIn(): Promise<boolean> {
  return (await fetchWithin("/api/me")).ok;
}

/** The address the visitor is signed in with, or null when there is no session or it cannot be read. */
export async function signedInEmail(): Promise<string | null> {
  try {
    const response = await fetchWithin("/api/me");

    if (!response.ok) return null;
    const body: { readonly email?: string | null } | null = await response.json().catch(() => null);

    return body?.email ?? null;
  } catch {
    return null;
  }
}

export type ClaimResult =
  | { readonly ok: true; readonly patientId: string }
  | { readonly ok: false; readonly error: string };

/** Claim a clinic-created patient record with the invitation token (AUT-020). */
export async function claimPatient(token: string): Promise<ClaimResult> {
  const response = await fetch("/api/claims", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });

  const body: { readonly patientId?: string; readonly error?: string } = await response
    .json()
    .catch(() => ({}));

  if (response.ok && body.patientId) return { ok: true, patientId: body.patientId };

  if (response.status === 401) return { ok: false, error: "signed_out" };

  return { ok: false, error: body.error ?? "failed" };
}

export async function startSession(turnstileToken: string): Promise<boolean> {
  const response = await fetch("/api/auth/sign-in/anonymous", {
    method: "POST",
    headers: { "content-type": "application/json", [TURNSTILE_HEADER]: turnstileToken },
    body: "{}",
  });

  return response.ok;
}

export type UploadOutcome =
  | { readonly status: "accepted" | "duplicate"; readonly documentId: string }
  | { readonly status: "rejected"; readonly reason: "too_large" | "unsupported_type" | "failed" };

interface UploadReply {
  readonly files?: readonly {
    readonly status?: string;
    readonly documentId?: string;
    readonly reason?: string;
  }[];
}

function outcomeFrom(reply: UploadReply | null): UploadOutcome {
  const file = reply?.files?.[0];

  if ((file?.status === "accepted" || file?.status === "duplicate") && file.documentId) {
    return { status: file.status, documentId: file.documentId };
  }

  if (file?.reason === "too_large" || file?.reason === "unsupported_type")
    return { status: "rejected", reason: file.reason };

  return { status: "rejected", reason: "failed" };
}

/** The upload's answer, or null when the server failed or sent something that is not JSON. */
function replyOf(request: XMLHttpRequest): UploadReply | null {
  if (request.status >= 500) return null;

  try {
    return JSON.parse(request.responseText || "null");
  } catch {
    return null;
  }
}

/**
 * One file, with progress. XMLHttpRequest rather than fetch, because fetch reports no upload
 * progress, and a phone photo of a lab report can take several seconds on a mobile connection.
 */
export function uploadFile(
  file: File,
  onProgress: (fraction: number) => void,
): Promise<UploadOutcome> {
  return new Promise((resolve) => {
    /* Sending a file the Worker will refuse takes as long as the connection is slow, and the person
       waits for an answer we already have. */
    if (file.size > MAX_LAB_FILE_BYTES) {
      resolve({ status: "rejected", reason: "too_large" });

      return;
    }

    const request = new XMLHttpRequest();
    const body = new FormData();

    body.append("files", file);
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });
    request.addEventListener("load", () => {
      resolve(outcomeFrom(replyOf(request)));
    });
    request.addEventListener("error", () => resolve({ status: "rejected", reason: "failed" }));
    request.open("POST", "/api/uploads");
    request.send(body);
  });
}

export type EmailOutcome = "sent" | "invalid" | "too_many" | "failed";

async function linkRequest(
  path: string,
  email: string,
  turnstileToken: string,
): Promise<EmailOutcome> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", [TURNSTILE_HEADER]: turnstileToken },
    body: JSON.stringify({ email }),
  });

  if (response.ok) return "sent";

  if (response.status === 400) return "invalid";

  return response.status === 429 ? "too_many" : "failed";
}

/** The email entered during onboarding, attached to the current session (AUT-002). */
export async function captureEmail(email: string, turnstileToken: string): Promise<EmailOutcome> {
  return linkRequest("/api/session/email", email, turnstileToken);
}

export type StaffAcceptance =
  | { readonly status: "ok" }
  | { readonly status: "error"; readonly code: string };

/** Takes up a Lattice staff invitation as the signed-in account (AUTH-017). */
export async function acceptStaffInvitation(token: string): Promise<StaffAcceptance> {
  const response = await fetch("/api/staff-invitations/accept", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });

  if (response.ok) return { status: "ok" };
  const refusal: { readonly error?: string } | null = await response.json().catch(() => null);

  return { status: "error", code: refusal?.error ?? "failed" };
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/sign-out", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
}

/** Signs out and says whether the Worker ended the session, so a failure can be shown. */
export async function trySignOut(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/sign-out", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    });

    return response.ok;
  } catch {
    return false;
  }
}

/** A new sign-in link for someone with no session, such as a customer whose link expired. */
export async function requestSignIn(email: string, turnstileToken: string): Promise<EmailOutcome> {
  return linkRequest("/api/sign-in", email, turnstileToken);
}

export type ClinicLoad =
  | { readonly status: "signed_out" }
  | { readonly status: "ok"; readonly home: ClinicHome }
  | { readonly status: "failed" };

export async function loadClinic(): Promise<ClinicLoad> {
  const response = await fetchWithin("/api/clinic");

  if (response.status === 401) return { status: "signed_out" };

  if (!response.ok) return { status: "failed" };
  const home: ClinicHome = await response.json();

  return { status: "ok", home };
}

export type ClinicWrite =
  | { readonly status: "ok"; readonly clinic: ClinicSnapshot }
  | { readonly status: "error"; readonly code: string };

/* Set by the clinic's step-up dialog while it is mounted. Resolves true once a fresh code passes. */
let askForStepUp: (() => Promise<boolean>) | null = null;

export function onStepUp(ask: (() => Promise<boolean>) | null): void {
  askForStepUp = ask;
}

/**
 * Inviting, changing or removing a member needs a second factor from the last 15 minutes. On
 * step_up_required the dialog asks for a code and the write is sent once more.
 */
async function clinicWrite(
  path: string,
  method: "POST" | "DELETE",
  body?: Readonly<Record<string, string | readonly string[]>>,
): Promise<ClinicWrite> {
  const first = await sendClinicWrite(path, method, body);

  if (first.status === "ok" || first.code !== "step_up_required" || askForStepUp === null)
    return first;

  return (await askForStepUp()) ? sendClinicWrite(path, method, body) : first;
}

async function sendClinicWrite(
  path: string,
  method: "POST" | "DELETE",
  body?: Readonly<Record<string, string | readonly string[]>>,
): Promise<ClinicWrite> {
  const response =
    body === undefined
      ? await fetch(path, { method })
      : await fetch(path, {
          method,
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });

  if (!response.ok) {
    const refusal: { readonly error?: string } | null = await response.json().catch(() => null);

    return { status: "error", code: refusal?.error ?? "failed" };
  }

  const clinic: ClinicSnapshot = await response.json();

  return { status: "ok", clinic };
}

export async function registerClinic(input: {
  readonly name: string;
  readonly displayName: string;
  readonly licenceNumber: string;
  readonly licenceState: string;
}): Promise<ClinicWrite> {
  return clinicWrite("/api/clinic", "POST", input);
}

export async function recordClinicLicence(input: {
  readonly licenceNumber: string;
  readonly licenceState: string;
}): Promise<ClinicWrite> {
  return clinicWrite("/api/clinic/licence", "POST", input);
}

export async function inviteClinicStaff(
  email: string,
  roles: readonly ClinicRole[],
): Promise<ClinicWrite> {
  return clinicWrite("/api/clinic/invitations", "POST", { email, roles });
}

export type ClinicInvitationLoad =
  | { readonly status: "ok"; readonly invitation: ClinicInvitationInfo }
  | { readonly status: "signed_out" }
  | { readonly status: "not_open" }
  | { readonly status: "failed" };

/** What the invited person is offered, read before they accept. */
export async function loadClinicInvitation(invitationId: string): Promise<ClinicInvitationLoad> {
  const response = await fetch(`/api/clinic/invitations/${encodeURIComponent(invitationId)}`);

  if (response.status === 401) return { status: "signed_out" };

  if (response.status === 404) return { status: "not_open" };

  if (!response.ok) return { status: "failed" };
  const invitation: ClinicInvitationInfo = await response.json();

  return { status: "ok", invitation };
}

export async function revokeClinicInvite(invitationId: string): Promise<ClinicWrite> {
  return clinicWrite(`/api/clinic/invitations/${encodeURIComponent(invitationId)}/revoke`, "POST");
}

export async function resendClinicInvite(invitationId: string): Promise<ClinicWrite> {
  return clinicWrite(`/api/clinic/invitations/${encodeURIComponent(invitationId)}/resend`, "POST");
}

export async function acceptClinicInvite(invitationId: string): Promise<ClinicWrite> {
  return clinicWrite("/api/clinic/invitations/accept", "POST", { invitationId });
}

export async function updateClinicMember(
  userId: string,
  roles: readonly ClinicRole[],
): Promise<ClinicWrite> {
  return clinicWrite(`/api/clinic/members/${encodeURIComponent(userId)}`, "POST", { roles });
}

/** Points this session at one of the caller's clinics and answers that clinic's home. */
export async function chooseClinic(organizationId: string): Promise<ClinicLoad> {
  const response = await fetch("/api/clinic/active", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ organizationId }),
  });

  if (response.status === 401) return { status: "signed_out" };

  if (!response.ok) return { status: "failed" };
  const home: ClinicHome = await response.json();

  return { status: "ok", home };
}

export async function removeClinicMember(userId: string): Promise<ClinicWrite> {
  return clinicWrite(`/api/clinic/members/${encodeURIComponent(userId)}`, "DELETE");
}

export async function assignClinicPatient(
  linkId: string,
  practitionerUserId: string,
): Promise<ClinicWrite> {
  return clinicWrite(`/api/clinic/links/${encodeURIComponent(linkId)}/assign`, "POST", {
    practitionerUserId,
  });
}

export type ClinicPatientsLoad =
  | { readonly status: "signed_out" }
  | { readonly status: "choose_clinic" }
  | { readonly status: "ok"; readonly clinic: ClinicPatientList }
  | { readonly status: "failed" };

export async function listClinicPatients(): Promise<ClinicPatientsLoad> {
  const response = await fetchWithin("/api/clinic/patients");

  if (response.status === 401) return { status: "signed_out" };

  if (response.status === 409) return { status: "choose_clinic" };

  if (!response.ok) return { status: "failed" };
  const clinic: ClinicPatientList = await response.json();

  return { status: "ok", clinic };
}

export type AddClinicPatientResult =
  | { readonly status: "ok"; readonly added: AddClinicPatientReply }
  | { readonly status: "conflict"; readonly code: "already_linked" | "already_pending" }
  | { readonly status: "error" };

export async function addClinicPatient(input: {
  readonly firstName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly practitionerUserId: string;
}): Promise<AddClinicPatientResult> {
  const response = await fetch("/api/clinic/patients", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });

  if (response.status === 409) {
    const refusal: { readonly error?: string } | null = await response.json().catch(() => null);

    if (refusal?.error === "already_linked" || refusal?.error === "already_pending")
      return { status: "conflict", code: refusal.error };

    return { status: "error" };
  }

  if (!response.ok) return { status: "error" };
  const added: AddClinicPatientReply = await response.json();

  return { status: "ok", added };
}

export type ClinicPatientLoad =
  | { readonly status: "ok"; readonly patient: ClinicPatientDetail }
  | { readonly status: "missing" };

export async function loadClinicPatient(patientId: string): Promise<ClinicPatientLoad> {
  const response = await fetchWithin(`/api/clinic/patients/${encodeURIComponent(patientId)}`);

  if (!response.ok) return { status: "missing" };
  const patient: ClinicPatientDetail = await response.json();

  return { status: "ok", patient };
}

export async function uploadClinicPatientFile(
  patientId: string,
  file: File,
): Promise<UploadOutcome> {
  const body = new FormData();

  body.append("files", file);

  const response = await fetch(`/api/clinic/patients/${encodeURIComponent(patientId)}/uploads`, {
    method: "POST",
    body,
  });

  const reply: UploadReply | null = response.ok ? await response.json() : null;

  return outcomeFrom(reply);
}

export async function setClinicPatientPractitioner(
  patientId: string,
  practitionerUserId: string,
): Promise<boolean> {
  const response = await fetch(`/api/clinic/patients/${encodeURIComponent(patientId)}/assign`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ practitionerUserId }),
  });

  return response.ok;
}

export async function retryClinicPatientDocument(
  patientId: string,
  documentId: string,
): Promise<boolean> {
  return (
    await fetch(
      `/api/clinic/patients/${encodeURIComponent(patientId)}/documents/${encodeURIComponent(documentId)}/retry`,
      { method: "POST" },
    )
  ).ok;
}

export async function removeClinicPatient(patientId: string): Promise<boolean> {
  return (
    await fetch(`/api/clinic/patients/${encodeURIComponent(patientId)}/remove`, { method: "POST" })
  ).ok;
}

export type ClinicFormulaLoad =
  | { readonly status: "ok"; readonly formula: ClinicFormulaView }
  | { readonly status: "missing" }
  | { readonly status: "pending" }
  /** The clinic account's role may not open this formula. */
  | { readonly status: "forbidden" }
  /** No answer came, or the server failed. */
  | { readonly status: "failed" };

async function clinicFormulaResponse(response: Response): Promise<ClinicFormulaLoad> {
  if (response.status >= 500) return { status: "failed" };

  if (response.status === 409) return { status: "pending" };

  if (response.status === 403) return { status: "forbidden" };

  if (!response.ok) return { status: "missing" };
  const formula: ClinicFormulaView | null = await response.json().catch(() => null);

  return formula === null ? { status: "failed" } : { status: "ok", formula };
}

export async function loadClinicFormula(patientId: string): Promise<ClinicFormulaLoad> {
  const response = await fetch(
    `/api/clinic/patients/${encodeURIComponent(patientId)}/formula`,
  ).catch(() => null);

  return response === null ? { status: "failed" } : clinicFormulaResponse(response);
}

export async function saveClinicFormulaIntake(
  patientId: string,
  answers: IntakeAnswers,
): Promise<ClinicFormulaLoad | { readonly status: "invalid" }> {
  const response = await fetch(
    `/api/clinic/patients/${encodeURIComponent(patientId)}/formula/intake`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(answers),
    },
  );

  if (response.status === 400) return { status: "invalid" };

  return clinicFormulaResponse(response);
}

export async function draftClinicFormula(
  patientId: string,
): Promise<
  ClinicFormulaLoad | { readonly status: "incomplete"; readonly missing: readonly string[] }
> {
  const response = await fetch(`/api/clinic/patients/${encodeURIComponent(patientId)}/formula`, {
    method: "POST",
  });

  if (response.status === 409) {
    const body: { readonly error?: string; readonly missing?: readonly string[] } | null =
      await response.json().catch(() => null);

    if (body?.error === "intake_incomplete")
      return { status: "incomplete", missing: body.missing ?? [] };

    return { status: "pending" };
  }

  return clinicFormulaResponse(response);
}

export async function editClinicFormula(
  patientId: string,
  edit: ClinicFormulaEdit,
): Promise<
  | ClinicFormulaLoad
  | { readonly status: "invalid" }
  | {
      readonly status: "blocked";
      readonly formula: ClinicFormulaView;
      readonly violations: ClinicFormulaView["violations"];
    }
> {
  const response = await fetch(
    `/api/clinic/patients/${encodeURIComponent(patientId)}/formula/edits`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ingredientId: edit.ingredientId,
        change: edit.change,
        dose: edit.dose,
      }),
    },
  );

  if (response.status === 400) return { status: "invalid" };

  if (response.status === 409) {
    const body: {
      readonly error?: string;
      readonly violations?: ClinicFormulaView["violations"];
    } | null = await response.json().catch(() => null);

    if (body?.error === "constraint_failed") {
      const loaded = await loadClinicFormula(patientId);

      /* The saved formula carries no violations, because the refused edit was never saved. The
         reasons are only in this response. */
      if (loaded.status === "ok")
        return { status: "blocked", formula: loaded.formula, violations: body.violations ?? [] };
    }

    return { status: "pending" };
  }

  return clinicFormulaResponse(response);
}

export async function submitClinicFormula(
  patientId: string,
): Promise<ClinicFormulaLoad | { readonly status: "unverified" }> {
  const response = await fetch(
    `/api/clinic/patients/${encodeURIComponent(patientId)}/formula/submit`,
    {
      method: "POST",
    },
  );

  if (response.status === 403) return { status: "unverified" };

  return clinicFormulaResponse(response);
}

export type ClinicFormulaChangeLoad =
  | { readonly status: "ok"; readonly change: ClinicFormulaChange }
  | { readonly status: "missing" }
  | { readonly status: "forbidden" }
  | { readonly status: "failed" };

export async function loadClinicFormulaChanges(
  patientId: string,
): Promise<ClinicFormulaChangeLoad> {
  const response = await fetch(
    `/api/clinic/patients/${encodeURIComponent(patientId)}/formula/changes`,
  ).catch(() => null);

  if (response === null || response.status >= 500) return { status: "failed" };

  if (response.status === 403) return { status: "forbidden" };

  if (!response.ok) return { status: "missing" };
  const change: ClinicFormulaChange | null = await response.json().catch(() => null);

  return change === null ? { status: "failed" } : { status: "ok", change };
}

export async function acknowledgeClinicFormulaChanges(patientId: string): Promise<boolean> {
  return (
    await fetch(
      `/api/clinic/patients/${encodeURIComponent(patientId)}/formula/changes/acknowledge`,
      { method: "POST" },
    )
  ).ok;
}

export type ClinicLinkLoad =
  | { readonly status: "signed_out" }
  | { readonly status: "ok"; readonly request: ClinicLinkRequestView }
  | { readonly status: "expired" }
  /** The account signed in cannot review link requests, such as a staff account. */
  | { readonly status: "forbidden" }
  | { readonly status: "missing" }
  /** The request never got an answer, or the server failed. */
  | { readonly status: "failed" };

async function clinicLinkResponse(pending: Promise<Response>): Promise<ClinicLinkLoad> {
  const response = await pending.catch(() => null);

  if (response === null || response.status >= 500) return { status: "failed" };

  if (response.status === 401) return { status: "signed_out" };

  if (response.status === 403) {
    const refusal: { readonly error?: string } | null = await response.json().catch(() => null);

    return { status: refusal?.error === "expired" ? "expired" : "forbidden" };
  }

  if (!response.ok) return { status: "missing" };
  const request: ClinicLinkRequestView | null = await response.json().catch(() => null);

  return request === null ? { status: "failed" } : { status: "ok", request };
}

export async function loadClinicLinkRequest(token: string): Promise<ClinicLinkLoad> {
  return clinicLinkResponse(fetch(`/api/clinic/link-requests/${encodeURIComponent(token)}`));
}

export async function acceptClinicLinkRequest(token: string): Promise<ClinicLinkLoad> {
  return clinicLinkResponse(
    fetch(`/api/clinic/link-requests/${encodeURIComponent(token)}/accept`, {
      method: "POST",
    }),
  );
}

export async function declineClinicLinkRequest(token: string): Promise<ClinicLinkLoad> {
  return clinicLinkResponse(
    fetch(`/api/clinic/link-requests/${encodeURIComponent(token)}/decline`, {
      method: "POST",
    }),
  );
}

interface IntakeFile {
  readonly answers?: Readonly<Record<string, string>>;
}

/** Saved intake answers for this session, so the questions resume where they left off (INT-004). */
export async function loadIntake(): Promise<IntakeAnswers | null> {
  const loaded = await loadIntakeOutcome();

  return loaded === "failed" ? null : loaded;
}

/** As `loadIntake`, but a server error is "failed" and not "no answers yet". */
export async function loadIntakeOutcome(): Promise<IntakeAnswers | null | "failed"> {
  const response = await fetchWithin("/api/intake");

  if (response.status >= 500) return "failed";

  if (!response.ok) return null;
  const body: IntakeFile = await response.json();

  const rows = Object.entries(body.answers ?? {}).map(([questionId, answerJson]) => ({
    questionId,
    answerJson,
  }));

  return readIntake(rows);
}

/** One answer, checked by the Worker against its question before it is stored (INT-031). */
export async function saveIntakeAnswer(
  questionId: QuestionId,
  answer: NonNullable<IntakeAnswers[QuestionId]>,
): Promise<boolean> {
  try {
    const response = await fetch(`/api/intake/${questionId}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(answer),
    });

    return response.ok;
  } catch {
    // A dropped connection rejects the fetch; the screen says the answer was not saved.
    return false;
  }
}

/** Draft a formula now that labs and the required questions are in. */
export async function requestDraft(): Promise<boolean> {
  return (await fetch("/api/formulas/draft", { method: "POST" })).ok;
}

/** The signed-in caller, as /api/me answers. Only the account id is used on support pages. */
export async function loadMe(): Promise<{ readonly userId: string } | null> {
  const response = await fetchWithin("/api/me");

  if (!response.ok) return null;
  const body: { readonly userId?: string } = await response.json();

  return body.userId ? { userId: body.userId } : null;
}

interface ReactionBody {
  description: string;
  severity?: string;
  email?: string;
}

/**
 * File a suspected reaction (SUP-004). A signed-in customer's goes against their formula. A
 * signed-out visitor gives an email to be written to, and may be told to wait after sending several.
 */
export async function reportReaction(
  description: string,
  severity: "mild" | "moderate" | "severe" | null,
  email: string | null,
): Promise<"sent" | "limited" | "failed"> {
  const report: ReactionBody = { description };

  if (severity !== null) report.severity = severity;

  if (email !== null) report.email = email;

  /* Offline or a dropped connection throws, and the form must still say the report did not send. */
  const response = await fetch("/api/reaction-reports", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(report),
  }).catch(() => null);

  if (response === null) return "failed";

  if (response.ok) return "sent";

  return response.status === 429 ? "limited" : "failed";
}

interface CurrentFormulaFile {
  readonly formula?: CurrentFormula | null;
}

/**
 * The in-review formula for this session. Null while drafting is still running, or when this
 * visitor has none.
 */
export async function loadCurrentFormula(): Promise<CurrentFormula | null> {
  const response = await fetchWithin("/api/formulas/current");

  if (!response.ok) return null;
  const body: CurrentFormulaFile = await response.json();

  return body.formula ?? null;
}

/**
 * The signed-in customer's formula home. `signed_out` when there is no session, null when the
 * answer is missing or malformed.
 */
export async function loadAccountIntake(): Promise<IntakeAnswers | "signed_out" | null> {
  const response = await fetchWithin("/api/intake");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  const body: IntakeFile = await response.json();

  const rows = Object.entries(body.answers ?? {}).map(([questionId, answerJson]) => ({
    questionId,
    answerJson,
  }));

  return readIntake(rows);
}

export async function saveAccountIntake(
  questionId: QuestionId,
  answer: NonNullable<IntakeAnswers[QuestionId]>,
): Promise<"saved" | "review" | "failed"> {
  const response = await fetch(`/api/intake/${questionId}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(answer),
  });

  if (!response.ok) return "failed";
  const body = readIntakeSave(await response.text());

  if (body === null) return "failed";

  return body.review ? "review" : "saved";
}

export async function loadClinics(): Promise<ClinicAccessList | "signed_out" | null> {
  const response = await fetchWithin("/api/account/clinics");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  return readClinicAccessList(await response.text());
}

export async function setClinicShare(
  linkId: string,
  shareScope: ClinicShareScope,
): Promise<ClinicAccessList | null> {
  const response = await fetch(`/api/account/clinics/${encodeURIComponent(linkId)}/share`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ shareScope }),
  });

  if (!response.ok) return null;

  return readClinicAccessList(await response.text());
}

export async function revokeClinic(linkId: string): Promise<ClinicAccessList | null> {
  const response = await fetch(`/api/account/clinics/${encodeURIComponent(linkId)}/revoke`, {
    method: "POST",
  });

  if (!response.ok) return null;

  return readClinicAccessList(await response.text());
}

export async function loadDeletion(): Promise<DeletionStatus | "signed_out" | null> {
  const response = await fetchWithin("/api/account/deletion");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  return readDeletionStatus(await response.text());
}

export async function requestDeletion(): Promise<DeletionStatus | null> {
  const response = await fetch("/api/account/deletion", { method: "POST" });

  if (!response.ok) return null;

  return readDeletionStatus(await response.text());
}

export async function downloadAccountData(): Promise<boolean> {
  const response = await fetchWithin("/api/account/export");

  if (!response.ok) return false;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "lattice-data.zip";
  link.click();
  URL.revokeObjectURL(url);

  return true;
}

export type AccountFormulaLoad = AccountFormulaEdit | "signed_out" | "missing" | "failed";

export async function loadAccountFormula(): Promise<AccountFormulaLoad> {
  const response = await fetchWithin("/api/account/formula");

  if (response.status === 401) return "signed_out";

  /* Only a 404 means there is no formula. Any other refusal is a failure, not an empty account. */
  if (response.status === 404) return "missing";

  if (!response.ok) return "failed";
  const view = readAccountFormulaEdit(await response.text());

  return view ?? "failed";
}

export type AccountFormulaResult =
  | { readonly ok: true; readonly view: AccountFormulaEdit }
  | { readonly ok: false; readonly error: string };

async function accountFormulaPost(
  path: string,
  body: Readonly<Record<string, string>>,
): Promise<AccountFormulaResult> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  const text = await response.text();

  if (!response.ok) {
    try {
      const parsed: { readonly error?: string } = JSON.parse(text);

      return { ok: false, error: parsed.error ?? "failed" };
    } catch {
      return { ok: false, error: "failed" };
    }
  }

  const view = readAccountFormulaEdit(text);

  return view === null ? { ok: false, error: "failed" } : { ok: true, view };
}

export async function removeAccountFormulaIngredient(
  ingredientId: string,
): Promise<AccountFormulaResult> {
  return accountFormulaPost("/api/account/formula/remove", { ingredientId });
}

export async function restoreAccountFormulaIngredient(
  ingredientId: string,
): Promise<AccountFormulaResult> {
  return accountFormulaPost("/api/account/formula/restore", { ingredientId });
}

export async function saveAccountFormulaOptions(body: {
  readonly flavor?: string;
  readonly proteinBaseIngredientId?: string;
}): Promise<AccountFormulaResult> {
  const payload: Record<string, string> = {};

  if (body.flavor !== undefined) payload["flavor"] = body.flavor;

  if (body.proteinBaseIngredientId !== undefined)
    payload["proteinBaseIngredientId"] = body.proteinBaseIngredientId;

  return accountFormulaPost("/api/account/formula/options", payload);
}

export async function loadAccountSubscription(): Promise<
  AccountSubscription | "signed_out" | "unconfirmed" | null
> {
  const response = await fetchWithin("/api/account/subscription");

  if (response.status === 401) return "signed_out";

  /* Billing is for an account with a confirmed email, which an upload alone does not make. Any
     other refusal, such as a held second step, is a failed load and not a request to confirm. */
  if (response.status === 403) {
    const body: { readonly error?: string } | null = await response.json().catch(() => null);

    return body?.error === "verified_account_required" ? "unconfirmed" : null;
  }

  if (!response.ok) return null;

  return readAccountSubscription(await response.text());
}

export type SubscriptionAction = "pause" | "skip" | "move" | "cancel" | "restart";

export async function changeAccountSubscription(
  action: SubscriptionAction,
  body: Readonly<Record<string, string>> = {},
): Promise<AccountSubscription | null> {
  const response = await fetch(`/api/account/subscription/${action}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) return null;

  return readAccountSubscription(await response.text());
}

export async function openAccountPortal(): Promise<AccountPortal | null> {
  const response = await fetch("/api/account/subscription/portal", { method: "POST" });

  if (!response.ok) return null;

  return readAccountPortal(await response.text());
}

export async function loadLabs(): Promise<AccountLabs | "signed_out" | null> {
  const response = await fetchWithin("/api/account/labs");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  return readAccountLabs(await response.text());
}

export async function loadAccountOrders(): Promise<CustomerOrders | "signed_out" | null> {
  const response = await fetchWithin("/api/account/orders");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  return readCustomerOrders(await response.text());
}

export async function loadAccountFormulas(): Promise<AccountFormulas | "signed_out" | null> {
  const response = await fetchWithin("/api/account/formulas");

  if (response.status === 401) return "signed_out";

  if (!response.ok) return null;

  return readAccountFormulas(await response.text());
}

export type CheckInReply = {
  readonly ok: true;
  readonly next: string | null;
};

/** Record a one-click check-in answer from its signed token (MSG-045). */
export async function answerCheckIn(token: string): Promise<CheckInReply | null> {
  const response = await fetch(`/api/check-in/${encodeURIComponent(token)}`, { method: "POST" });

  if (!response.ok) return null;

  const body: { readonly next?: string | null } = await response.json();

  return { ok: true, next: body.next ?? null };
}

/** Stop marketing email for the recipient named by a signed footer token (MSG-042). */
export async function unsubscribe(token: string): Promise<boolean> {
  return (await fetch(`/api/email/unsubscribe/${encodeURIComponent(token)}`, { method: "POST" }))
    .ok;
}

/** Live lab-reading progress for this patient, or null when there is no session yet. */
export async function loadProgress(): Promise<ProcessingProgress | null> {
  const response = await fetchWithin("/api/processing/snapshot");

  if (!response.ok) return null;
  const body: ProcessingProgress = await response.json();

  return body;
}

export type CheckoutLoad = CheckoutView | "signed_out" | "missing" | "declined" | "failed";

/**
 * The formula waiting at checkout, or why it is not there: "missing" is the Worker's 404 (no
 * formula yet), "declined" the same 404 once a physician has declined the formula, "failed" any
 * other refusal or server error.
 */
export async function loadCheckout(): Promise<CheckoutLoad> {
  const response = await fetchWithin("/api/checkout");

  if (response.status === 401) return "signed_out";

  if (response.status === 404) {
    const reply: { readonly error?: string } = await response.json().catch(() => ({}));

    return reply.error === "declined" ? "declined" : "missing";
  }

  if (!response.ok) return "failed";
  const body: CheckoutView = await response.json();

  return body;
}

export type CheckoutResult =
  | { readonly ok: true; readonly view: CheckoutView }
  | { readonly ok: false; readonly error: string };

async function checkoutPost(
  path: string,
  body: Readonly<Record<string, string>>,
): Promise<CheckoutResult> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const reply: { readonly error?: string } = await response.json().catch(() => ({}));

    return { ok: false, error: reply.error ?? "failed" };
  }

  const view: CheckoutView = await response.json();

  return { ok: true, view };
}

export async function removeCheckoutIngredient(ingredientId: string): Promise<CheckoutResult> {
  return checkoutPost("/api/checkout/remove", { ingredientId });
}

export async function restoreCheckoutIngredient(ingredientId: string): Promise<CheckoutResult> {
  return checkoutPost("/api/checkout/restore", { ingredientId });
}

export async function saveCheckoutOptions(body: {
  readonly flavor?: string;
  readonly proteinBaseIngredientId?: string;
}): Promise<CheckoutResult> {
  return checkoutPost("/api/checkout/options", body);
}

export async function checkCheckoutAddress(address: ShippingAddress): Promise<string | null> {
  const response = await fetch("/api/checkout/address", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(address),
  });

  if (response.ok) return null;
  const reply: { readonly error?: string } = await response.json().catch(() => ({}));

  return reply.error ?? "failed";
}

export type PayResult =
  | { readonly ok: true; readonly paid: true }
  | { readonly ok: true; readonly url: string }
  | { readonly ok: false; readonly error: string };

export async function requestCheckoutPay(address: ShippingAddress): Promise<PayResult> {
  const response = await fetch("/api/checkout/pay", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(address),
  });

  const reply: { readonly error?: string; readonly url?: string; readonly paid?: boolean } =
    await response.json().catch(() => ({}));

  if (reply.url) return { ok: true, url: reply.url };

  if (reply.paid === true) return { ok: true, paid: true };

  return { ok: false, error: reply.error ?? "failed" };
}

export type MeLoad =
  | { readonly status: "signed_out" }
  | { readonly status: "ok"; readonly me: MeView }
  | { readonly status: "failed" };

/** The whole of /api/me, for pages that route on roles or the second factor. */
export async function loadSession(): Promise<MeLoad> {
  const response = await fetchWithin("/api/me");

  if (response.status === 401) return { status: "signed_out" };

  if (!response.ok) return { status: "failed" };
  const me: MeView = await response.json();

  return { status: "ok", me };
}

export interface SecurityStatus {
  readonly required: boolean;
  readonly passed: boolean;
  readonly totp: boolean;
  readonly passkeys: number;
  readonly backupCodes: number;
}

export async function loadSecurity(): Promise<SecurityStatus | "signed_out" | null> {
  const response = await fetchWithin("/api/security");

  if (response.status === 401) return "signed_out";

  return response.ok ? response.json() : null;
}

export interface TotpStart {
  readonly otpauthUrl: string;
  readonly secret: string;
  readonly backupCodes: readonly string[];
}

export type TotpStartResult =
  | { readonly status: "ok"; readonly start: TotpStart }
  | { readonly status: "error"; readonly code: string };

export async function startTotp(): Promise<TotpStartResult> {
  const response = await fetch("/api/security/totp", { method: "POST" });

  const body: Partial<TotpStart> & { readonly error?: string } = await response
    .json()
    .catch(() => ({}));

  if (response.ok && body.otpauthUrl && body.secret && body.backupCodes)
    return {
      status: "ok",
      start: { otpauthUrl: body.otpauthUrl, secret: body.secret, backupCodes: body.backupCodes },
    };

  return { status: "error", code: body.error ?? "failed" };
}

/** A code sent for checking: "ok", or the Worker's refusal code such as "wrong_code". */
async function sendCode(path: string, code: string): Promise<string> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });

  if (response.ok) return "ok";
  const body: { readonly error?: string } = await response.json().catch(() => ({}));

  return body.error ?? "failed";
}

export async function confirmTotp(code: string): Promise<string> {
  return sendCode("/api/security/totp/confirm", code);
}

export async function verifySecondFactor(code: string): Promise<string> {
  return sendCode("/api/security/verify", code);
}

export type CodeSignIn = "ok" | "wrong" | "too_many" | "failed";

/**
 * Signs in with the emailed code, through Better Auth. An upload's anonymous session is folded into
 * the account by Better Auth's anonymous plugin, as a magic link does.
 */
export async function signInWithCode(email: string, code: string): Promise<CodeSignIn> {
  const response = await fetch("/api/auth/sign-in/email-otp", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, otp: code }),
  });

  if (response.ok) return "ok";

  if (response.status === 400) return "wrong";

  return response.status === 403 || response.status === 429 ? "too_many" : "failed";
}
